<template>
  <div>
    <asc-page-loading-indicator
      :loading="dataLoading && !apiPageFailure.length"
    >
      <div class="asc-airline-mgmt-container">
        <asc-airline-mgmt-template
          :tabs="tabs"
          :onboarding-values="allAirlines"
          :add-airline="handleAddAirlineClicked"
          :on-open-airline="handleOpenDialog"
          :agreement-values="allAgreements"
          :add-agreement="handleAddAgreementClicked"
          @tabChange="handleTabChanged"
        ></asc-airline-mgmt-template>

        <asc-airline-mgmt-dialog
          :show-dialog="airlineDialogVisible && !apiPageFailure.length"
          :on-close-dialog="handleCloseDialog"
          :edit="editAirline"
          :airlines="allAirlines"
          :airline-data="airlineDetails"
          :dialog-loading="airlineDialogLoading"
          :button-loading="airlineButtonLoading"
          @submitAirline="handleOnSubmitClicked"
        ></asc-airline-mgmt-dialog>

        <asc-airline-agreement-dialog
          :show-dialog="agreementDialogVisible && !apiPageFailure.length"
          :button-loading="agreementButtonLoading"
          :on-close-dialog="handleCloseAgreementDialog"
          :agreements="agreementTypes"
          :airlines="allAirlines"
          :platforms="masterModels"
          :agreement-data="agreementData"
          @submitAgreement="handleSubmitNewAgreement"
        ></asc-airline-agreement-dialog>

        <asc-confirmation
          v-if="displayToastNotification"
          :definition="definition"
          :on-close="closeToastNotification"
        ></asc-confirmation>
      </div>
    </asc-page-loading-indicator>
  </div>
</template>

<script>
import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import AscAirlineMgmtTemplate from '@/components/templates/AscAirlineMgmtTemplate'
import AscAirlineMgmtDialog from '@/components/molecules/AscAirlineMgmtDialog.vue'
import {
  AIRLINE_MANAGEMENT_CONSTS as AOC,
  LOOKUP_CONSTS as LC,
  USER_CONSTS as UC,
} from '@/stores/consts'
import { mapState, mapActions } from 'vuex'
import AscConfirmation from '@/components/molecules/AscConfirmation'
import AscAirlineAgreementDialog from '@/components/molecules/AscAirlineAgreementDialog.vue'

export default {
  name: 'AscAirlineMgmtContainer',

  components: {
    AscPageLoadingIndicator,
    AscAirlineMgmtTemplate,
    AscAirlineMgmtDialog,
    AscConfirmation,
    AscAirlineAgreementDialog,
  },

  data() {
    return {
      isAirlineOnboardingLoading: false,
      isAirlineAgreementLoading: false,
      tabs: [
        {
          id: 1,
          title: 'Airline Onboarding',
        },
        {
          id: 2,
          title: 'Airline Agreement',
        },
      ],
      airlineDialogVisible: false,
      agreementDialogVisible: false,
      editAirline: false,
      airlineDetails: {},
      airlineButtonLoading: false,
      airlineDialogLoading: false,
      displayToastNotification: false,
      definition: [
        {
          mode: 'toast',
          type: 'success',
          text: '',
          position: 'bottom',
          dismissible: true,
          autoClose: true,
        },
      ],
      agreementButtonLoading: false,
      agreementData: null,
    }
  },

  computed: {
    ...mapState({ apiPageFailure: 'apiPageFailure' }),
    ...mapState(AOC.NAMESPACE, [
      'allAirlines',
      'airline',
      'newAirlineToast',
      'editAirlineToast',
      'allAgreements',
      'newAgreementToast',
      'agreementTypes',
    ]),
    ...mapState(LC.NAMESPACE, ['masterModels']),
    dataLoading() {
      return this.isAirlineOnboardingLoading || this.isAirlineAgreementLoading
    },
  },

  created() {
    this.getAirlineOnboardingData()
  },

  methods: {
    ...mapActions(AOC.NAMESPACE, [
      AOC.GET_ALL_AIRLINES,
      AOC.GET_AIRLINE,
      AOC.SAVE_UPDATED_AIRLINE,
      AOC.SAVE_NEW_AIRLINE,
      AOC.GET_ALL_AGREEMENTS,
      AOC.SAVE_NEW_AGREEMENT,
      AOC.GET_AGREEMENT_TYPES,
    ]),
    ...mapActions(LC.NAMESPACE, [LC.GET_MASTER_MODELS]),
    ...mapActions(UC.NAMESPACE, [UC.SET_USER_AIRLINES]),
    async getAirlineOnboardingData() {
      this.isAirlineOnboardingLoading = true
      await Promise.all([this[AOC.GET_ALL_AIRLINES]()])
      this.isAirlineOnboardingLoading = false
    },
    async getAirlineAgreementData() {
      this.isAirlineAgreementLoading = true
      await Promise.all([
        this[AOC.GET_ALL_AGREEMENTS](),
        this[AOC.GET_AGREEMENT_TYPES](),
        this[LC.GET_MASTER_MODELS](),
      ])
      this.isAirlineAgreementLoading = false
    },
    async handleSubmitNewAirline(data) {
      this.airlineButtonLoading = true
      await Promise.all([this[AOC.SAVE_NEW_AIRLINE]({ ...data, legacyId: 0 })])
      this.updateGlobalFilterAirlines(data)
      if (this.newAirlineToast) {
        this.definition[0].text = 'Airline saved successfully'
        this.displayToastNotification = true
      }
      this.airlineButtonLoading = false
      this.airlineDialogVisible = false
      this.airlineDetails = {}
    },
    updateGlobalFilterAirlines(airlineName) {
      // get the new airline by filtering from all airlines
      const newAirlineObjIndex = this.allAirlines.findIndex(
        airline => airline.name === airlineName.name,
      )
      if (newAirlineObjIndex >= 0)
        this[UC.SET_USER_AIRLINES](this.allAirlines[newAirlineObjIndex])
    },
    async handleSubmitEditAirline(data) {
      this.editAirline = false
      this.airlineButtonLoading = true
      await Promise.all([this[AOC.SAVE_UPDATED_AIRLINE](data)])
      if (this.editAirlineToast) {
        this.definition[0].text = 'Airline saved successfully'
        this.displayToastNotification = true
      }
      this.airlineButtonLoading = false
      this.airlineDialogVisible = false
      this.airlineDetails = {}
    },
    async handleSubmitNewAgreement(data) {
      this.agreementButtonLoading = true
      await Promise.all([this[AOC.SAVE_NEW_AGREEMENT](data)])
      if (this.newAgreementToast) {
        this.definition[0].text = 'Agreement(s) saved successfully'
        this.displayToastNotification = true
      }
      this.agreementButtonLoading = false
      this.agreementDialogVisible = false
    },
    handleTabChanged(tab) {
      if (tab === 0 && !this.allAirlines?.length) {
        this.getAirlineOnboardingData()
      }
      if (tab === 1 && !this.allAgreements?.length) {
        this.getAirlineAgreementData()
      }
    },
    handleOpenDialog(data) {
      this.editAirline = true
      this.airlineDialogVisible = true
      this.airlineDialogLoading = true
      this.airlineDetails = {
        ...data,
      }
      this.airlineDialogLoading = false
    },
    handleAddAirlineClicked() {
      this.airlineDetails = {}
      this.editAirline = false
      this.airlineDialogVisible = true
    },
    handleOnSubmitClicked(data) {
      if (this.editAirline) {
        this.handleSubmitEditAirline(data)
      } else {
        this.handleSubmitNewAirline(data)
      }
    },
    handleCloseDialog() {
      this.airlineDialogVisible = false
      this.editAirline = false
      this.airlineDetails = {}
    },
    handleAddAgreementClicked() {
      this.agreementDialogVisible = true
      this.agreementData = {}
    },
    handleCloseAgreementDialog() {
      this.agreementDialogVisible = false
      this.agreementData = {}
    },
    closeToastNotification() {
      this.displayToastNotification = false
    },
  },
}
</script>

<style lang="scss">
.asc-airline-mgmt-container {
  background-color: $gray-100;
}
</style>
